/**
 * tenantBranding — pure white-label brand-identity resolution (tenant-foundation
 * Task 18; extended by demo-tenant-environment Task 22). UI-free +
 * side-effect-free so the precedence rules can be unit-tested directly.
 * `BrandingContext` consumes this to resolve the five brand-identity values
 * (name, logo, primary color, accent color, header text) it exposes.
 *
 * THE NEUTRAL FALLBACK IS THE POINT (Task 22, Req 1.3, 1.4). Every fallback in
 * this module is tenant-AGNOSTIC. It is never the original client's identity,
 * because the fallback is exactly what a sales-demo viewer sees while the read is
 * in flight, when the record is absent, and when the read blows the 3s budget —
 * i.e. the three moments where an "Idaho Pageants" default would leak the real
 * client into someone else's demo. That default used to live here (and in
 * `BrandingContext.DEFAULT_BRANDING`); it is gone on purpose. Do not reintroduce
 * a tenant-specific literal as a default value anywhere in this file.
 */

/** The minimal Membership shape this resolver needs (a subset of `MembershipSummary`). */
export interface BrandNameMembership {
  tenantId: string;
  tenantName: string;
}

/**
 * The neutral, tenant-agnostic brand name. Generic by construction: it names no
 * tenant, so it cannot leak one. Every "we don't know yet / we couldn't find it"
 * path in the branding stack lands here.
 */
export const NEUTRAL_BRAND_NAME = 'Pageant Portal';

/**
 * The five brand-identity values a surface needs, all resolved — no `undefined`
 * holes for a component to paper over with a literal of its own.
 *
 * `logoS3Key` / `primaryColor` / `accentColor` are `null` rather than a neutral
 * stand-in: "no logo" (Req 1.3/1.4 says the neutral identity has none) and "no
 * color override" (the app renders its own shipped design tokens) are genuinely
 * the absence of a value, and a null forces the consumer to handle that.
 */
export interface BrandIdentity {
  /** Displayed tenant name. Always non-blank. */
  name: string;
  /** Logo object key, or `null` for the neutral identity (no logo). */
  logoS3Key: string | null;
  /** Tenant primary color, or `null` to leave the shipped design token alone. */
  primaryColor: string | null;
  /** Tenant accent color, or `null` to leave the shipped design token alone. */
  accentColor: string | null;
  /** Header/tagline copy. Empty string when unresolved — never invented copy. */
  headerText: string;
  /**
   * `true` when the `BrandingConfig` read has NOT produced a usable record
   * (in flight, absent, failed, or past the 3s budget), so the identity is the
   * neutral fallback. Consumers use it to suppress branded chrome rather than
   * render a half-branded header.
   */
  isNeutral: boolean;
}

/** The neutral tenant-agnostic identity: generic name, no logo, no color override. */
export const NEUTRAL_BRAND_IDENTITY: BrandIdentity = Object.freeze({
  name: NEUTRAL_BRAND_NAME,
  logoS3Key: null,
  primaryColor: null,
  accentColor: null,
  headerText: '',
  isNeutral: true,
});

/** The `BrandingConfig` fields this resolver reads (a subset of the GraphQL type). */
export interface BrandingConfigFields {
  pageantName?: string | null;
  logoS3Key?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  headerText?: string | null;
}

function trimmedOrNull(value: string | null | undefined): string | null {
  const t = typeof value === 'string' ? value.trim() : '';
  return t.length > 0 ? t : null;
}

/**
 * Resolve the effective brand (pageant) name for the active surface.
 *
 * Precedence:
 *   1. The active tenant's own name — the canonical `Tenant.name`, surfaced
 *      client-side as the matching Membership's `tenantName`. This is what makes
 *      the authenticated app read as the active tenant (e.g. "Meridian Pageants").
 *   2. The year-keyed `BrandingConfig.pageantName` (the single-tenant / public
 *      default — Idaho's own site has no active-tenant claim).
 *   3. A neutral fallback.
 *
 * A blank/absent `activeTenantId` (public / pre-auth) skips straight to the
 * fetched/default name, so the public marketing site stays Idaho's.
 */
export function resolveBrandName(
  activeTenantId: string | null | undefined,
  memberships: ReadonlyArray<BrandNameMembership>,
  fetchedPageantName: string | null | undefined,
  fallback: string = NEUTRAL_BRAND_NAME,
): string {
  const id = typeof activeTenantId === 'string' ? activeTenantId.trim() : '';
  if (id) {
    // Trim BOTH sides. Trimming only the claim was a latent asymmetry: a tenantId
    // carrying stray whitespace would fail to match its own claim and silently fall
    // through to the default brand. Real tenant ids have no whitespace, so this is
    // unreachable today — but a comparison that trims one operand and not the other
    // is the kind of thing that becomes reachable later, and trimming both can never
    // match two genuinely distinct ids.
    const active = memberships.find((m) => m.tenantId?.trim() === id);
    const name = active?.tenantName?.trim();
    if (name) return name;
  }
  const fetched = fetchedPageantName?.trim();
  return fetched && fetched.length > 0 ? fetched : fallback;
}
/** Inputs to full brand-identity resolution. */
export interface ResolveBrandIdentityInput {
  /** The verified active-tenant claim (`AuthUser.activeTenantId`), if any. */
  activeTenantId?: string | null;
  /** The viewer's own Memberships — the source of the active tenant's canonical name. */
  memberships?: ReadonlyArray<BrandNameMembership>;
  /**
   * The retrieved `BrandingConfig` record, or `null`/`undefined` when the read is
   * still in flight, returned nothing, failed, or exceeded the 3s budget. All
   * three collapse to the same neutral answer on purpose (Req 1.3, 1.4).
   */
  config?: BrandingConfigFields | null;
}

/**
 * Resolve all five brand-identity values (Task 22, Req 1.1, 1.3, 1.4).
 *
 * WITH a usable `BrandingConfig`, every value comes from that record — the whole
 * point being that no component supplies its own. The NAME additionally honors
 * the tenant-foundation Task 18 overlay: the active tenant's canonical
 * `Tenant.name` outranks the year-keyed `pageantName`, so the authenticated app
 * reads as the tenant the viewer is actually in.
 *
 * WITHOUT one, the answer is `NEUTRAL_BRAND_IDENTITY` — generic name, no logo, no
 * color override, no header copy — except that a resolved active-tenant name
 * still wins over the generic name. That exception is deliberate and it is not a
 * leak: that name is the viewer's OWN tenant, read from their own verified
 * membership, so a Meridian prospect whose branding read stalls still sees
 * "Meridian Pageants" (Req 1.2) rather than a generic placeholder, and can never
 * see some other tenant's name. `isNeutral` stays `true` in that case, because
 * the branding record itself is still unresolved.
 */
export function resolveBrandIdentity(input: ResolveBrandIdentityInput): BrandIdentity {
  const { activeTenantId, memberships = [], config } = input;

  const pageantName = trimmedOrNull(config?.pageantName);
  const activeTenantName = resolveBrandName(activeTenantId, memberships, null, '');

  // A record with no usable name is treated as absent: half a brand is worse than
  // the neutral one, and Req 1.4 makes absence a neutral-fallback case.
  if (!pageantName) {
    return {
      ...NEUTRAL_BRAND_IDENTITY,
      name: activeTenantName || NEUTRAL_BRAND_NAME,
    };
  }

  return {
    name: activeTenantName || pageantName,
    logoS3Key: trimmedOrNull(config?.logoS3Key),
    primaryColor: trimmedOrNull(config?.primaryColor),
    accentColor: trimmedOrNull(config?.accentColor),
    headerText: trimmedOrNull(config?.headerText) ?? '',
    isNeutral: false,
  };
}

/**
 * Map a resolved identity's colors onto the app's design tokens.
 *
 * Colors travel as CSS custom properties, never as inline `style` on individual
 * components: `--primary` / `--accent` are the tokens `tokens.css` declares and
 * every Tailwind utility in the app already reads (`bg-primary`, `text-accent`,
 * `border-primary`, …), so overriding them re-skins the whole surface from data.
 * The legacy `--branding-*` names are emitted alongside for the older consumers
 * that read them directly (e.g. `PublicProfile`).
 *
 * The NEUTRAL identity contributes NO properties, so the shipped tokens stand.
 * That is what keeps an unresolved read from producing a half-recolored page.
 */
export function brandIdentityCssVars(
  identity: Pick<BrandIdentity, 'primaryColor' | 'accentColor'>,
): Record<string, string> {
  const vars: Record<string, string> = {};
  if (identity.primaryColor) {
    vars['--primary'] = identity.primaryColor;
    vars['--branding-primary-color'] = identity.primaryColor;
  }
  if (identity.accentColor) {
    vars['--accent'] = identity.accentColor;
    vars['--branding-accent-color'] = identity.accentColor;
  }
  return vars;
}
